import clsx from 'clsx';
import { isValid, parse } from 'date-fns';
import { format } from 'date-fns';
import { Calendar, CalendarProps } from 'react-date-range';
import { FieldError } from 'react-hook-form';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { vars } from 'src/core/themes';
import { Popover, Stack, TooltipPopover } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import {
  CrossIcon,
  DownloadIcon,
  IconsFill,
  InfoOutlineIcon,
  ProcessingIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { ContentIds } from 'src/utils/constants/contentIdDataMap';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';

import { getTextFieldState, PosTextField } from '../PosTextField';
import * as styles from './DatePicker.css';

export const ReportSnapshotInput = ({
  value,
  date,
  fieldError,
  disabled,
  className,
  dateDisplayFormat = 'MM/dd/yyyy',
  color = vars.color.backgroundHighlight,
  locale = getLocaleFromLanguageOrCurrent(),
  onDateChange,
  onDownloadSnapshot,
  isSnapshotDownloading,
  textFieldStyles,
  useSiteTimeZone = true,
  ...props
}: Omit<CalendarProps, 'onChange'> & {
  value?: string | null | Date;
  onDateChange?: (d: Date) => void;
  fieldError?: string | FieldError | undefined;
  disabled?: boolean;
  textFieldStyles?: React.CSSProperties;
  useSiteTimeZone?: boolean;
  onDownloadSnapshot: () => void;
  isSnapshotDownloading?: boolean;
}) => {
  const { toZonedTime, fromZonedTime } = useSiteTimezoneContext();
  const reportSnapshotDescriptionText = useContent(
    ContentId.ReportSnapshotDescription
  );

  date = date ?? (value ? new Date(value) : undefined);
  if (useSiteTimeZone) {
    if (date) {
      date = toZonedTime(date);
    }
    if (props.minDate) {
      props.minDate = toZonedTime(props.minDate);
    }
    if (props.maxDate) {
      props.maxDate = toZonedTime(props.maxDate);
    }
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild disabled={disabled}>
        <Stack
          className={styles.snapshotPickerRootContainer}
          direction="row"
          gap="s"
          title={reportSnapshotDescriptionText}
        >
          <PosTextField
            rootProps={{
              state: getTextFieldState(fieldError),
              disabled: disabled,
            }}
            placeholder={ContentIds[ContentId.ChooseSnapshotDate].defaultValue}
            value={date ? format(date, dateDisplayFormat) : undefined}
            onChange={(e) => {
              const str = e.target.value;
              const newDate = parse(str, dateDisplayFormat, new Date(), {
                locale: getLocaleFromLanguageOrCurrent(),
              });
              if (isValid(newDate)) {
                onDateChange?.(
                  useSiteTimeZone ? fromZonedTime(newDate) : newDate
                );
              }
            }}
            disabled={disabled}
            prefixDisplay={
              <TooltipPopover
                variant="link"
                contentVariant="dark"
                triggerContent={<InfoOutlineIcon size={vars.iconSize.m} />}
              >
                <Content id={ContentId.ReportSnapshotDescription} />
              </TooltipPopover>
            }
            postfixDisplay={
              date ? (
                isSnapshotDownloading ? (
                  <RotatingWrapper>
                    <ProcessingIcon
                      fill={IconsFill.textBrand}
                      size={vars.iconSize.l}
                    />
                  </RotatingWrapper>
                ) : (
                  <DownloadIcon
                    withHoverEffect
                    fill={IconsFill.textBrand}
                    align="middle"
                    disabled={disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onDownloadSnapshot();
                    }}
                  />
                )
              ) : null
            }
          />
        </Stack>
      </Popover.Trigger>
      <Popover.Content align="start">
        <Popover.Close asChild className={styles.datePickerClose}>
          <CrossIcon withHoverEffect />
        </Popover.Close>
        <Calendar
          {...props}
          date={date}
          disabledDay={disabled ? () => true : undefined}
          className={clsx(className, styles.datePicker)}
          dateDisplayFormat={dateDisplayFormat}
          color={color}
          locale={locale}
          onChange={(d) =>
            onDateChange?.(useSiteTimeZone ? fromZonedTime(d) : d)
          }
        />
      </Popover.Content>
    </Popover.Root>
  );
};
